import { useState } from 'react';
import { Button } from '@/components/ui/button';
import type { PriorReport } from '@fixtyer/shared';
import { clsx } from 'clsx';

interface PriorReportsProps {
  reports: (PriorReport & { daysBefore?: number | null })[];
  hasLocation: boolean;
  hasPostcode: boolean;
  isSearching: boolean;
  searchCompleted?: boolean; // Whether a search has already been done (e.g., during claim creation)
  onSearch: () => Promise<{ found: number; reportUrl: string | null }>;
  onConfirm: (reportId: string) => void;
  onUnlink: (reportId: string) => void;
  readOnly?: boolean;
}

export function PriorReportsSection({ 
  reports, 
  hasLocation,
  hasPostcode,
  isSearching,
  searchCompleted = false,
  onSearch, 
  onConfirm, 
  onUnlink,
  readOnly = false,
}: PriorReportsProps) {
  const [hasSearched, setHasSearched] = useState(searchCompleted);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleSearch = async () => {
    setSearchError(null);
    try {
      await onSearch();
      setHasSearched(true);
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : 'Search failed');
    }
  };

  const confirmedReports = reports.filter(r => r.userConfirmedMatch);
  const unconfirmedReports = reports.filter(r => !r.userConfirmedMatch);

  // No location or postcode yet
  if (!hasLocation && !hasPostcode) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-surface-100 rounded-full flex items-center justify-center shrink-0">
            <span className="text-sm">📍</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-brand-950">Add a location first</p>
            <p className="text-xs text-surface-500">Mac will search for prior reports once you set the incident location</p>
          </div>
        </div>
      </div>
    );
  }

  // Has confirmed reports - show success state
  if (confirmedReports.length > 0) {
    return (
      <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-2xl border-2 border-success-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-success-500 rounded-xl flex items-center justify-center shadow-lg shadow-success-500/30">
            <span className="text-2xl">🎯</span>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-success-800">
              Prior Report Confirmed!
            </h2>
            <p className="text-sm text-success-700">
              This significantly strengthens your claim
            </p>
          </div>
        </div>
        
        <div className="p-4 bg-white/70 rounded-xl border border-success-200 mb-4">
          <p className="text-sm text-success-800">
            <strong>🏆 Excellent!</strong> You've confirmed that this pothole was reported before your incident. 
            This proves the council had <strong>prior knowledge</strong> of the defect, which undermines their 
            Section 58 defence and greatly increases your chances of a successful claim.
          </p>
        </div>

        <div className="space-y-3">
          {confirmedReports.map((report) => (
            <PriorReportCard
              key={report.id}
              report={report}
              onConfirm={() => {}}
              onUnlink={() => onUnlink(report.id)}
              readOnly={readOnly}
            />
          ))}
        </div>

        {unconfirmedReports.length > 0 && (
          <div className="mt-6 pt-6 border-t border-success-200">
            <p className="text-sm text-success-700 mb-3">
              Other nearby reports ({unconfirmedReports.length}):
            </p>
            <div className="space-y-3">
              {unconfirmedReports.map((report) => (
                <PriorReportCard
                  key={report.id}
                  report={report}
                  onConfirm={() => onConfirm(report.id)}
                  onUnlink={() => onUnlink(report.id)}
                  confirmDisabled={true} // Already have a confirmed report
                  readOnly={readOnly}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Has unconfirmed reports - prompt to confirm
  if (reports.length > 0) {
    return (
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-300 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
            <span className="text-2xl">📋</span>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-amber-900">
              Prior Reports Found!
            </h2>
            <p className="text-sm text-amber-700">
              {reports.length} report{reports.length !== 1 ? 's' : ''} found near your location
            </p>
          </div>
        </div>
        
        <div className="p-4 bg-white/70 rounded-xl border border-amber-200 mb-4">
          <p className="text-sm text-amber-800">
            <strong>👆 Action required:</strong> Review the reports below and click 
            "This is my pothole" if any of them match the pothole that damaged your vehicle. 
            This is the <strong>single most important thing</strong> you can do to strengthen your claim.
          </p>
        </div>

        <div className="space-y-3">
          {reports.map((report) => (
            <PriorReportCard
              key={report.id}
              report={report}
              onConfirm={() => onConfirm(report.id)}
              onUnlink={() => onUnlink(report.id)}
              readOnly={readOnly}
            />
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-amber-200">
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={handleSearch}
            loading={isSearching}
          >
            Search again
          </Button>
        </div>
      </div>
    );
  }

  // No reports yet - show search UI or results
  return (
    <div className="bg-white rounded-2xl border border-surface-200 p-6">
      {searchError && (
        <div className="p-3 bg-error-50 border border-error-200 rounded-lg mb-4">
          <p className="text-sm text-error-700">{searchError}</p>
        </div>
      )}

      {!hasSearched ? (
        // Haven't searched yet - auto search on mount or show button
        <div className="text-center py-4">
          <Button 
            onClick={handleSearch} 
            loading={isSearching}
            className="gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Search for Prior Reports
          </Button>
          <p className="text-xs text-surface-500 mt-2">
            Mac will search for pothole reports within 100m of your incident location
          </p>
        </div>
      ) : (
        // Searched but found nothing - compact version
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-surface-100 rounded-full flex items-center justify-center shrink-0">
            <span className="text-sm">🔍</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-brand-950">No prior reports found</p>
            <p className="text-xs text-surface-500">No reports of this pothole before your incident</p>
          </div>
        </div>
      )}
    </div>
  );
}

interface PriorReportCardProps {
  report: PriorReport & { daysBefore?: number | null };
  onConfirm: () => void;
  onUnlink: () => void;
  confirmDisabled?: boolean;
  readOnly?: boolean;
}

function PriorReportCard({ report, onConfirm, onUnlink, confirmDisabled = false, readOnly = false }: PriorReportCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={clsx(
        'border rounded-xl p-4 transition-all',
        report.userConfirmedMatch
          ? 'border-success-400 bg-white shadow-sm'
          : 'border-surface-200 bg-white hover:border-amber-300 hover:shadow-sm'
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {report.userConfirmedMatch && (
              <span className="px-2 py-0.5 bg-success-500 text-white text-xs font-medium rounded-full">
                ✓ Confirmed Match
              </span>
            )}
            <span
              className={clsx(
                'px-2 py-0.5 text-xs rounded-full font-medium',
                report.reportState === 'open'
                  ? 'bg-orange-100 text-orange-700'
                  : report.reportState === 'fixed'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-surface-100 text-surface-600'
              )}
            >
              {report.reportState}
            </span>
          </div>
          <h3 className="font-medium text-brand-950">
            {report.reportTitle || 'Pothole Report'}
          </h3>
          <p className="text-sm text-surface-700 mt-1">
            {report.reportDate
              ? `Reported ${new Date(report.reportDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}`
              : 'Report date unknown'}
            {report.daysBefore != null && report.daysBefore > 0 && (
              <span className="font-semibold text-brand-700">
                {' '}— {report.daysBefore} days before your incident
              </span>
            )}
          </p>
          {report.distanceMeters !== undefined && report.distanceMeters !== null && (
            <p className="text-sm text-surface-500 mt-1">
              📍 {Math.round(report.distanceMeters)}m from your location
            </p>
          )}
        </div>
      </div>

      {expanded && report.reportDetail && (
        <div className="mt-4 p-3 bg-surface-50 rounded-lg">
          <p className="text-sm text-surface-700">{report.reportDetail}</p>
        </div>
      )}

      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-surface-100">
        {report.reportUrl && (
          <a
            href={report.reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-brand-600 hover:text-brand-700 font-medium inline-flex items-center gap-1"
          >
            View on FixMyStreet
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
        {report.reportDetail && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-surface-500 hover:text-surface-700"
          >
            {expanded ? 'Hide details' : 'Show details'}
          </button>
        )}
        <div className="flex-1" />
        {report.userConfirmedMatch ? (
          !readOnly && (
            <Button variant="ghost" size="sm" onClick={onUnlink}>
              Remove
            </Button>
          )
        ) : (
          !readOnly && (
            <Button 
              variant={confirmDisabled ? "secondary" : "primary"} 
              size="sm" 
              onClick={onConfirm}
              disabled={confirmDisabled}
            >
              {confirmDisabled ? 'Not my pothole' : 'This is my pothole'}
            </Button>
          )
        )}
      </div>
    </div>
  );
}

// Keep the old export for backwards compatibility
export { PriorReportsSection as PriorReports };
